import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Activity,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Power,
  Search,
  Trash2,
  UserPlus,
  Users
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import StatPill from '../../components/ui/StatPill';
import Avatar from '../../components/ui/Avatar';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import Skeleton from '../../components/ui/Skeleton';
import { Table, TBody, TH, THead, TR, TD, TableScroller } from '../../components/ui/Table';
import ProvisionAccountForm from '../../components/admin/ProvisionAccountForm';
import EditUserModal from '../../components/admin/EditUserModal';
import ResetPasswordModal from '../../components/admin/ResetPasswordModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import { getAdminOverview, updateAdminUser, deleteAdminUser } from '../../utils/api';
import { roleBadge, relativeTime, formatDate } from '../../utils/commandCenterHelpers';

export default function TeamPage() {
  const navigate = useNavigate();
  const { user: me } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [menuId, setMenuId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminOverview();
      setUsers(res.data.users);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    let list = [...users];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((u) => u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q));
    if (roleFilter !== 'all') list = list.filter((u) => u.role === roleFilter);
    return list;
  }, [users, search, roleFilter]);

  async function toggleActive(u) {
    setBusyId(u.id);
    setMenuId(null);
    try {
      // If currently deactivated (active === false), reactivate; otherwise deactivate.
      await updateAdminUser(u.id, { active: u.active === false });
      toast.success(u.active === false ? `Reactivated @${u.username}` : `Deactivated @${u.username}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Update failed');
    } finally {
      setBusyId(null);
      load();
    }
  }

  async function confirmDelete() {
    if (!deleteUser) return;
    setBusyId(deleteUser.id);
    try {
      await deleteAdminUser(deleteUser.id);
      toast.success(`Deleted @${deleteUser.username}`);
      setDeleteUser(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="Team management"
        subtitle="Provision accounts, assign roles, reset credentials, and control who can sign in."
        icon={Users}
        pills={[<StatPill key="n" icon={Users} label="Accounts" value={users.length} />]}
        actions={
          <Button
            variant="secondary"
            leftIcon={UserPlus}
            onClick={() => setProvisionOpen(true)}
            className="bg-white/10 border-white/15 text-white hover:bg-white/20"
          >
            New account
          </Button>
        }
      />

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-soft">
        <div className="px-5 py-3.5 border-b border-slate-100 flex flex-col sm:flex-row gap-2.5">
          <Input
            leftIcon={Search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or username"
            className="sm:flex-1"
          />
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="sm:w-44">
            <option value="all">All roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="viewer">Viewer</option>
          </Select>
        </div>

        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="py-14 text-center">
            <p className="text-[14px] font-medium text-slate-700">No accounts found</p>
            <p className="text-[12.5px] text-slate-500 mt-1">Adjust your filters or provision a new account.</p>
          </div>
        ) : (
          <TableScroller>
            <Table>
              <THead>
                <TR hoverable={false}>
                  <TH>User</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                  <TH>Saves</TH>
                  <TH>Last activity</TH>
                  <TH>Created</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {visible.map((u) => {
                  const badge = roleBadge(u.role);
                  const isSelf = u.id === me?.id;
                  return (
                    <TR key={u.id}>
                      <TD>
                        <button
                          type="button"
                          onClick={() => navigate(`/command-center/users/${u.id}`)}
                          className="flex items-center gap-2.5 text-left group"
                        >
                          <Avatar name={u.name} size="sm" />
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold text-slate-900 group-hover:text-brand-700 truncate">
                              {u.name} {isSelf && <span className="text-slate-400 font-normal">(you)</span>}
                            </p>
                            <p className="text-[11.5px] text-slate-500 truncate">@{u.username}</p>
                          </div>
                        </button>
                      </TD>
                      <TD>
                        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                      </TD>
                      <TD>
                        {u.active === false ? (
                          <Badge variant="danger" size="sm" dot>Deactivated</Badge>
                        ) : (
                          <Badge variant="success" size="sm" dot>Active</Badge>
                        )}
                      </TD>
                      <TD className="tabular-nums">{u.saveCount}</TD>
                      <TD className="text-slate-500">{u.lastActivityAt ? relativeTime(u.lastActivityAt) : '—'}</TD>
                      <TD className="text-slate-500">{formatDate(u.createdAt)}</TD>
                      <TD className="text-right">
                        <RowMenu
                          open={menuId === u.id}
                          onOpen={() => setMenuId(menuId === u.id ? null : u.id)}
                          onClose={() => setMenuId(null)}
                          busy={busyId === u.id}
                          isSelf={isSelf}
                          active={u.active !== false}
                          onActivity={() => navigate(`/command-center/users/${u.id}`)}
                          onEdit={() => { setMenuId(null); setEditUser(u); }}
                          onReset={() => { setMenuId(null); setResetUser(u); }}
                          onToggle={() => toggleActive(u)}
                          onDelete={() => { setMenuId(null); setDeleteUser(u); }}
                        />
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </TableScroller>
        )}
      </div>

      {/* Provision modal */}
      <Modal
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
        title="Provision new account"
        description="Create an admin or viewer. Super admins are promoted via Edit."
        icon={UserPlus}
        size="md"
      >
        <ProvisionAccountForm
          embedded
          onCreated={() => {
            setProvisionOpen(false);
            load();
          }}
        />
      </Modal>

      <EditUserModal open={!!editUser} user={editUser} onClose={() => setEditUser(null)} onSaved={load} />
      <ResetPasswordModal open={!!resetUser} user={resetUser} onClose={() => setResetUser(null)} />
      <ConfirmDialog
        open={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        onConfirm={confirmDelete}
        loading={busyId === deleteUser?.id}
        title="Delete account"
        confirmLabel="Delete permanently"
        description={deleteUser ? `@${deleteUser.username} will be permanently removed.` : ''}
      >
        <p className="text-[13px] text-slate-600">
          Their authored version history is retained for audit, but the account cannot be recovered.
        </p>
      </ConfirmDialog>
    </>
  );
}

function RowMenu({ open, onOpen, onClose, busy, isSelf, active, onActivity, onEdit, onReset, onToggle, onDelete }) {
  return (
    <div className="relative inline-block">
      <Button size="iconSm" variant="ghost" onClick={onOpen} loading={busy} aria-label="Actions">
        {!busy && <MoreHorizontal className="h-4 w-4" />}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={onClose} />
          <div className="absolute right-0 z-30 mt-1 w-48 rounded-xl border border-slate-200 bg-white shadow-pop py-1 animate-slide-up">
            <MenuItem icon={Activity} label="View activity" onClick={onActivity} />
            <MenuItem icon={Pencil} label="Edit account" onClick={onEdit} />
            <MenuItem icon={KeyRound} label="Reset password" onClick={onReset} />
            {!isSelf && (
              <MenuItem
                icon={Power}
                label={active ? 'Deactivate' : 'Reactivate'}
                onClick={onToggle}
                tone={active ? 'warning' : 'success'}
              />
            )}
            {!isSelf && (
              <>
                <div className="my-1 border-t border-slate-100" />
                <MenuItem icon={Trash2} label="Delete" onClick={onDelete} tone="danger" />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, tone = 'default' }) {
  const toneClass = {
    default: 'text-slate-700 hover:bg-slate-50',
    warning: 'text-amber-700 hover:bg-amber-50',
    success: 'text-emerald-700 hover:bg-emerald-50',
    danger: 'text-rose-700 hover:bg-rose-50'
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors ${toneClass}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
